import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    Users, GraduationCap, BookOpen, Building, UserPlus, CreditCard,
    Clock, Activity, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ManagerDashboard = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/dashboard/stats`, {
                    withCredentials: true
                });
                setStats(response.data);
            } catch (error) {
                console.error('Failed to fetch stats:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, []);

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="manager-dashboard">
                {/* Welcome Section */}
                <div className="bg-gradient-to-r from-[#064E3B] to-[#047857] rounded-xl p-6 text-white">
                    <h1 className="text-2xl font-bold font-['Outfit'] mb-2">Manager Dashboard</h1>
                    <p className="text-white/80">Overview of your assigned programs and operations</p>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">Total Students</p>
                                    <p className="text-3xl font-bold text-[#1F2937] tabular-nums">{stats?.total_students || 0}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-[#064E3B]/10">
                                    <GraduationCap className="h-6 w-6 text-[#064E3B]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">Total Teachers</p>
                                    <p className="text-3xl font-bold text-[#1F2937] tabular-nums">{stats?.total_teachers || 0}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-[#D97706]/10">
                                    <BookOpen className="h-6 w-6 text-[#D97706]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">Waiting List</p>
                                    <p className="text-3xl font-bold text-[#1F2937] tabular-nums">{stats?.waiting_list || 0}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-[#F59E0B]/10">
                                    <UserPlus className="h-6 w-6 text-[#F59E0B]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">Pending Leave</p>
                                    <p className="text-3xl font-bold text-[#1F2937] tabular-nums">{stats?.pending_leave_requests || 0}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-[#10B981]/10">
                                    <Clock className="h-6 w-6 text-[#10B981]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Card className="card-surface">
                        <CardHeader>
                            <CardTitle className="font-['Outfit']">Quick Actions</CardTitle>
                            <CardDescription>Common management tasks</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <Link to="/manager/waiting-list">
                                <Button variant="outline" className="w-full justify-start" data-testid="manager-waiting-list">
                                    <UserPlus className="h-4 w-4 mr-2" />
                                    Review Waiting List
                                    {stats?.waiting_list > 0 && (
                                        <Badge className="ml-auto bg-[#D97706]">{stats.waiting_list}</Badge>
                                    )}
                                </Button>
                            </Link>
                            <Link to="/manager/leave-requests">
                                <Button variant="outline" className="w-full justify-start" data-testid="manager-leave">
                                    <Clock className="h-4 w-4 mr-2" />
                                    Manage Leave Requests
                                    {stats?.pending_leave_requests > 0 && (
                                        <Badge className="ml-auto bg-[#F59E0B]">{stats.pending_leave_requests}</Badge>
                                    )}
                                </Button>
                            </Link>
                            <Link to="/manager/students">
                                <Button variant="outline" className="w-full justify-start" data-testid="manager-students">
                                    <GraduationCap className="h-4 w-4 mr-2" />
                                    Manage Students
                                </Button>
                            </Link>
                            <Link to="/manager/accounts">
                                <Button variant="outline" className="w-full justify-start" data-testid="manager-accounts">
                                    <CreditCard className="h-4 w-4 mr-2" />
                                    View Accounts
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>

                    <Card className="card-surface">
                        <CardHeader>
                            <CardTitle className="font-['Outfit']">Programs Overview</CardTitle>
                            <CardDescription>Your assigned programs</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-col items-center justify-center py-8">
                                <Building className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                <p className="text-[#4B5563] mb-4">View and manage programs</p>
                                <Link to="/manager/programs">
                                    <Button className="btn-primary" data-testid="view-programs">
                                        <ArrowRight className="h-4 w-4 mr-2" />
                                        View Programs
                                    </Button>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default ManagerDashboard;
